import React, { useState, useEffect, useCallback, useMemo } from "react";
// ✅ Path imports for MUI components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Collapse from "@mui/material/Collapse";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import useTheme from "@mui/material/styles/useTheme";

// ✅ Path imports for MUI icons
import CalendarToday from "@mui/icons-material/CalendarToday";
import AccessTime from "@mui/icons-material/AccessTime";
import ShoppingBag from "@mui/icons-material/ShoppingBag";
import DirectionsCar from "@mui/icons-material/DirectionsCar";
import LocalShipping from "@mui/icons-material/LocalShipping";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  setPickupLocation,
  setDropoffLocation,
  setPickUpTime,
  setDeliveryTransportType,
  setPickupCoordinates,
  setDropoffCoordinates,
  setDistanceInfo,
  setTargetTime,
} from "../redux/deliverySlice";
import MapsAutoComplete from "./MapsAutoComplete";
import {
  getCoordinatesByPlaceId,
  getDirectionsByPlaceIds,
} from "../utils/LocationFunctions";
import { generateTimeOptions } from "../utils/TimeFunctions";
import colors from "../styles/colors";

const LocationScreen = ({ onSubmit }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [timeEdited, setTimeEdited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Get current state from Redux
  const delivery = useSelector((state) => state.delivery.delivery);
  const {
    from,
    fromPlaceId,
    to,
    toPlaceId,
    pickUpTime,
    deliveryTransportType,
  } = delivery;


  // Memoize date calculation to prevent unnecessary re-renders
  const [date, setDate] = useState(() => {
    return pickUpTime
      ? new Date(pickUpTime).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10);
  });

  // Memoize time options to prevent regeneration on every render
  const timeOptions = useMemo(() => generateTimeOptions(), []);

  // Memoize size options to prevent recreation on every render
  const sizeOptions = useMemo(() => [
    { value: "public", icon: <ShoppingBag />, label: t("smallSize") },
    { value: "car", icon: <DirectionsCar />, label: isMobile? t("mediumSize").split("kokoinen")[0] :t("mediumSize") },
    { value: "van", icon: <LocalShipping />, label: t("largeSize") },
  ], [t]);

  // Extract target time calculation logic
  const calculateTargetTime = useCallback((baseTime, duration = 0) => {
    const twoHours = 2 * 60 * 60 * 1000;
    return baseTime + duration + twoHours;
  }, []);

  useEffect(() => {
    const fetchDirections = async () => {
      if (fromPlaceId && toPlaceId) {
        setIsLoading(true);
        try {
          const directions = await getDirectionsByPlaceIds(
            fromPlaceId,
            toPlaceId
          );
          if (directions) {
            const durationMs = directions.duration.value * 1000;
            
            // Dispatch the distance and duration to Redux
            dispatch(
              setDistanceInfo({
                distance: directions.distance.value, // meters
                duration: directions.duration.value, // seconds
              })
            );
            
            const estimateTargetTime = new Date().getTime() + durationMs;
            const sixHoursFromNow = new Date().getTime() + (6 * 60 * 60 * 1000);
            
            if (estimateTargetTime > sixHoursFromNow) {
              const targetTime = calculateTargetTime(estimateTargetTime);
              dispatch(setTargetTime({ targetTime }));
            }
          }
        } catch (error) {
          console.error("Error fetching directions:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchDirections();
  }, [fromPlaceId, toPlaceId, dispatch, calculateTargetTime]);

  const handleSizeChange = useCallback((event, newSize) => {
    if (newSize !== null) {
      dispatch(setDeliveryTransportType(newSize));
    }
  }, [dispatch]);

  const handleDateChange = useCallback((e) => {
    const newDate = e.target.value;
    setDate(newDate);
    const newDateTime = new Date(newDate).getTime();
    dispatch(setPickUpTime({ pickUpTime: newDateTime }));
    
    const duration = delivery.duration || 0;
    const targetTime = calculateTargetTime(newDateTime, duration);
    dispatch(setTargetTime({ targetTime }));
  }, [dispatch, delivery.duration, calculateTargetTime]);

  const handleTimeChange = useCallback((e) => {
    setTimeEdited(true);
    const newDateTime = new Date(`${date}T${e.target.value}`).getTime();
    dispatch(setPickUpTime({ pickUpTime: newDateTime }));
    
    const duration = delivery.duration || 0;
    const targetTime = calculateTargetTime(newDateTime, duration);
    dispatch(setTargetTime({ targetTime }));
  }, [date, dispatch, delivery.duration, calculateTargetTime]);

  const handleLocationChange = useCallback(async (location, isPickup = true) => {
    const setLocationAction = isPickup ? setPickupLocation : setDropoffLocation;
    const setCoordinatesAction = isPickup ? setPickupCoordinates : setDropoffCoordinates;

    if (location) {
      const { description: address, place_id: placeId } = location;
      dispatch(setLocationAction({ address, placeId }));

      try {
        const locationData = await getCoordinatesByPlaceId(placeId);
        if (locationData) {
          dispatch(setCoordinatesAction({
            latitude: locationData.latitude,
            longitude: locationData.longitude,
            postalCode: locationData.postalCode
          }));
        }
      } catch (error) {
        console.error(`Error fetching coordinates for ${isPickup ? 'pickup' : 'dropoff'}:`, error);
      }
    } else {
      dispatch(setLocationAction({ address: "", placeId: "" }));
      dispatch(setCoordinatesAction({ 
        latitude: null, 
        longitude: null, 
        postalCode: null 
      }));
    }
  }, [dispatch]);

  const handlePickupChange = useCallback((location) => {
    handleLocationChange(location, true);
  }, [handleLocationChange]);

  const handleDropoffChange = useCallback((location) => {
    handleLocationChange(location, false);
  }, [handleLocationChange]);

  // Format time display value
// Format time display value - round to nearest 15 minutes to match options
const timeDisplayValue = useMemo(() => {
  if (!pickUpTime) return "";
  
  const date = new Date(pickUpTime);
  const minutes = date.getMinutes();
  
  // Round to nearest 15-minute interval
  const roundedMinutes = Math.round(minutes / 15) * 15;
  
  // Handle case where rounding goes to next hour
  if (roundedMinutes === 60) {
    date.setHours(date.getHours() + 1);
    date.setMinutes(0);
  } else {
    date.setMinutes(roundedMinutes);
  }
  
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}, [pickUpTime]);

  // Get submit button text (currently both cases return the same, but kept for future differentiation)
  const submitText = useMemo(() => {
    return deliveryTransportType === "public" ? t("buttonOther") : t("buttonOther");
  }, [deliveryTransportType, t]);

  const isFormValid = useMemo(() => {
    return !isLoading && from && to;
  }, [isLoading, from, to]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1, sm: 2 },
        backgroundColor: "transparent",
      }}
    >
      <Stack spacing={isMobile ? 1.5 : 2}>
        {/* Pickup Location */}
        <Box sx={{ position: "relative" }}>
          <MapsAutoComplete
            isMobile={isMobile}
            label={t("pickupPlaceholder")}
            value={from}
            onChange={handlePickupChange}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#ffffff",
                fontSize: isMobile ? "16px" : "14px", // Prevent zoom on iOS
              },
            }}
          />
        </Box>

        {/* Dropoff Location */}
        <MapsAutoComplete
          isMobile={isMobile}
          label={t("dropoffPlaceholder")}
          value={to}
          onChange={handleDropoffChange}
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: "#ffffff",
              fontSize: isMobile ? "16px" : "14px", // Prevent zoom on iOS
            },
          }}
        />

        {/* Date and Time Selection */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            type="date"
            variant="outlined"
            value={date}
            onChange={handleDateChange}
            InputProps={{
              startAdornment: !isMobile && (
                <IconButton size="small" tabIndex={-1}>
                  <CalendarToday fontSize="small" />
                </IconButton>
              ),
              sx: {
                borderRadius: 2,
                backgroundColor: "#ffffff",
                height: isMobile ? "48px" : "56px",
                fontSize: isMobile ? "16px" : "14px", // Prevent zoom on iOS
              },
            }}
            size={isMobile ? "small" : "medium"}
            inputProps={{
              min: new Date().toISOString().slice(0, 10), // Prevent past dates
            }}
          />

          <Select
            fullWidth
            value={timeDisplayValue}
            onChange={handleTimeChange}
            displayEmpty
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            startAdornment={
              !isMobile && (
                <IconButton size="small" tabIndex={-1}>
                  <AccessTime fontSize="small" />
                </IconButton>
              )
            }
            renderValue={(selected) => {
              return timeEdited ? selected : `${t("pickUpTime")} ${t("now").toLowerCase()}`;
            }}
            sx={{
              borderRadius: 2,
              height: isMobile ? "48px" : "56px",
              backgroundColor: "#ffffff",
              fontSize: isMobile ? "16px" : "14px", // Prevent zoom on iOS
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: isMobile ? 36 * 4.5 : 48 * 4.5,
                },
              },
            }}
          >
            {timeOptions.map((timeOption) => (
              <MenuItem 
                key={timeOption} 
                value={timeOption} 
                dense={isMobile}
                sx={{
                  fontSize: isMobile ? "16px" : "14px", // Consistent font size
                }}
              >
                {timeOption}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Transport Type Selection */}
        <Box sx={{ my: 1 }}>
          {isMobile ? (
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "8px",
            }}>
              {sizeOptions.map((option) => (
                <Button
                  key={option.value}
                  variant={
                    deliveryTransportType === option.value
                      ? "contained"
                      : "outlined"
                  }
                  startIcon={option.icon}
                  onClick={() =>
                    dispatch(setDeliveryTransportType(option.value))
                  }
                  fullWidth
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    borderRadius: 2,
                    backgroundColor:
                      deliveryTransportType === option.value
                        ? colors.primary
                        : "transparent",
                    color:
                      deliveryTransportType === option.value
                        ? colors.textPrimary
                        : "inherit",
                    borderColor: colors.primary,
                    "&:hover": {
                      backgroundColor:
                        deliveryTransportType === option.value
                          ? colors.primary
                          : "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  {option.label}
                </Button>
              ))}
           </div>
          ) : (
            <ToggleButtonGroup
              value={deliveryTransportType}
              exclusive
              onChange={handleSizeChange}
              aria-label={t("deliverySize")}
              sx={{
                width: "100%",
                display: "flex",
                "& .MuiToggleButton-root": {
                  flex: 1,
                  p: 1,
                  borderRadius: 2,
                  border: `1px solid ${colors.primary}`,
                  m: 0.5,
                  "&.Mui-selected": {
                    backgroundColor: colors.primary,
                    color: colors.textPrimary,
                    "&:hover": {
                      backgroundColor: colors.primary,
                    },
                  },
                },
              }}
            >
              {sizeOptions.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.icon} <Box sx={{ ml: 1 }}>{option.label}</Box>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </Box>

        {/* Transport Type Description */}
        <Collapse in={Boolean(deliveryTransportType)}>
          <FormHelperText
            sx={{
              textAlign: "center",
              fontSize: "0.875rem",
              color: "text.secondary",
              mt: 0.5,
            }}
          >
            {deliveryTransportType === "public" && t("smallSizeDetails")}
            {deliveryTransportType === "car" && t("mediumSizeDetails")}
            {deliveryTransportType === "van" && t("largeSizeDetails")}
          </FormHelperText>
        </Collapse>

        {/* Submit Button */}
        <Button
          variant="contained"
          fullWidth
          sx={{
            py: isMobile ? 1.5 : 2,
            mt: 1,
            backgroundColor: colors.primary,
            color: colors.textPrimary,
            borderRadius: 2,
            boxShadow: 2,
            fontSize: isMobile ? "16px" : "14px", // Prevent zoom on iOS
            "&:hover": {
              backgroundColor: colors.primary,
              opacity: 0.9,
            },
            "&:disabled": {
              backgroundColor: "rgba(0, 0, 0, 0.12)",
              color: "rgba(0, 0, 0, 0.26)",
            },
          }}
          onClick={onSubmit}
          disabled={!isFormValid}
        >
          {isLoading ? t("loading") : submitText}
        </Button>
      </Stack>
    </Paper>
  );
};

export default LocationScreen;